import { castArray } from 'lodash';

import { JobType } from 'interfaces/job';
import { useJobs } from 'utils/queryHooks/jobs/useJobs';

import { PREVIOUS_JOBS_PAGE_SIZE } from './constants';
import { usesBatchParentJobs } from './jobQueryOptions';

export interface PreviousJobsAvailability {
  /** True once we know the study has at least one prior job of the given type. */
  hasPreviousJobs: boolean;
  /** True while we are still determining whether prior jobs exist. */
  isCheckingPreviousJobs: boolean;
}

/**
 * Reports whether prior jobs exist for the given job type under the current jobs query context
 * (including any study filter derived by `useJobs`). Action forms use this to hide the
 * "Reuse previous job settings" step when there is nothing to load.
 *
 * Configure this query to match the first-page request made by `LoadFromPreviousJobStep` so
 * enabling that step does not require an extra network request.
 */
export const usePreviousJobsAvailability = (jobType: JobType | JobType[]): PreviousJobsAvailability => {
  const { data, isLoading } = useJobs({
    page: 1,
    pageSize: PREVIOUS_JOBS_PAGE_SIZE,
    withChildren: !usesBatchParentJobs(jobType),
    additionalFilters: { types: castArray(jobType) },
    noInitialData: true,
  });

  return {
    hasPreviousJobs: (data?.totalJobs ?? 0) > 0,
    isCheckingPreviousJobs: isLoading,
  };
};
